import * as qs from "querystring";
import * as React from "react";
import { Paths } from "shared/paths";
import Box from "../../components/box";
import { CartMain as Container } from "./main";
import Progress from "react-svg-progress/dist";
import { useUnauthorizedPrompt } from "client/hooks";
import { useLocation } from "wouter";
import { ApiError, Orders } from "client/http";
import { Auth, Cart } from "client/stores";
import { AmbiguousRate } from "kmmp/orders/v2";
import { getRateValue, showSaturdayRates } from "client/modules/shipping";
import { Link } from "wouter";
import { Rate } from "shipping-ups";
import { Sentry } from "client/modules/sentry";
import { MdCheck as CheckIcon } from "react-icons/md";
import { CONSTANTS } from "client/constants";
import { Option, compute } from "@nozzlegear/railway";
import { CreateOrderRequest } from "api";
import { mapToCreateOrderReqLineItem } from "shared/map-line-items";
import { Observer } from "mobx-react";
import { countImages } from "client/image-urls";
import { If } from "shared/components";
import { useImmer } from "use-immer";
import { useState } from "react";
import { getOrCreateIdempotencyKey, IDEMPOTENCY_SESSION_KEY } from "./idempotency-key";

type Props = {};

interface State {
    error: Option<string>;
    placingOrder: boolean;
    selectedRate: Rate | AmbiguousRate;
    email: string;
    phone: string;
    placedBy: string;
    neededByDate: string;
    neededByTime: string;
    shippingMethodConsent: boolean;
}

export function CartDetailsPage(_: Props): JSX.Element {
    const shipping = Cart.shipping;
    let secondDayRate: Option<Rate> = Option.ofNone();
    let selectedRateForState: Rate | AmbiguousRate = shipping.ambiguous ? "Second Day Air" : shipping.rates[0];

    if (shipping.ambiguous === false) {
        const rate = shipping.rates.find((rate) => rate.Service.Code === "02");

        secondDayRate = compute<Option<Rate>>(() => {
            if (rate) return Option.ofSome(rate);

            return shipping.rates.length > 0 ? Option.ofSome(shipping.rates[0]) : Option.ofNone();
        });

        if (Option.isSome(secondDayRate)) {
            selectedRateForState = Option.get(secondDayRate);
        }
    }

    const handleUnauthorized = useUnauthorizedPrompt();
    const [location, setLocation] = useLocation();
    // Lazily initialized once on mount. Keyed to item uuids and PO IDs so any cart change produces
    // a new key rather than replaying a prior submission.
    const [idempotencyKey] = useState(() => getOrCreateIdempotencyKey(Cart.items));
    const [state, setState] = useImmer<State>({
        error: Option.ofNone(),
        selectedRate: selectedRateForState,
        placingOrder: false,
        email: "",
        neededByDate: "",
        neededByTime: "",
        phone: "",
        placedBy: "",
        shippingMethodConsent: false,
    });

    const getFamilyCharge = () => {
        if (typeof state.selectedRate === "string") {
            return 0;
        }

        if (Option.isNone(secondDayRate)) {
            return 0;
        }

        const result = getRateValue(state.selectedRate) - Option.get(secondDayRate.map(getRateValue));

        return result < 0 ? 0 : result;
    };

    async function handleContinue(e: React.SyntheticEvent) {
        e.preventDefault();

        let validationError = Option.ofNone<string>();

        if (state.placingOrder) {
            return;
        }

        const purchaseOrderIds = Cart.items
            .map((li) => li.purchaseOrderId)
            .filter((id): id is string => id != null);

        Sentry.addBreadcrumb({
            message: "User initiated order submission",
            category: "Kmmp.Browser.Cart.Details",
            level: "info",
            data: {
                purchaseOrderIds,
                lineItemCount: Cart.items.length,
            },
        });

        setState(state => ({ ...state, placingOrder: true, error: Option.ofNone() }));

        // Validate details
        if (!state.email || !/@.*\./i.test(state.email)) {
            validationError = Option.ofSome(`You must enter a valid email address.`);
        } else if (/@.*@.*/.test(state.email) || /\w+ +\w+/.test(state.email.trim())) {
            validationError = Option.ofSome(
                "It looks like you entered more than one email address. This is not currently supported, please use only one email address."
            );
        } else if (!state.placedBy) {
            validationError = Option.ofSome(`You must enter your full name.`);
        } else if (!state.phone) {
            validationError = Option.ofSome(`You must enter your phone number.`);
        } else if (!state.neededByDate || !state.neededByTime) {
            validationError = Option.ofSome(`You must enter a Needed By date and time.`);
        } else if (Cart.shipping.ambiguous && !state.shippingMethodConsent) {
            validationError = Option.ofSome("Please check the box to indicate that you understand that your shipping method may incur additional charges.");
        }

        if (Option.isSome(validationError)) {
            setState(state => ({ ...state, placingOrder: false, error: validationError }));
            return;
        }

        const shipping = compute<CreateOrderRequest.Shipping>(() => {
            const {
                address: { ...cartAddr },
                shipping,
            } = Cart;
            const addr: CreateOrderRequest.ShippingAddress = {
                city: cartAddr.city,
                country: cartAddr.country,
                name: cartAddr.name,
                state: cartAddr.state,
                street1: cartAddr.street1,
                street2: cartAddr.street2,
                street3: cartAddr.street3,
                zip: cartAddr.zip,
            };

            if (shipping.ambiguous) {
                const ambig: CreateOrderRequest.Shipping = {
                    ...addr,
                    ambiguous: true,
                    rate_service: state.selectedRate as AmbiguousRate,
                    shipping_method_consent: true,
                };

                return ambig;
            }

            const selectedRate = state.selectedRate as Rate;
            const unambig: CreateOrderRequest.Shipping = {
                ambiguous: false,
                // Only SCI has a family charge
                family_charge: Auth.companySettings.companyName === "sci" ? getFamilyCharge() : 0,
                rate_service: selectedRate.Service.Name,
                rate_service_code: selectedRate.Service.Code,
                rate_value: getRateValue(selectedRate),
                rate_carrier: "UPS",
                saturday_delivery_available: shipping.saturdayDeliveryAvailable,
                ...addr,
            };

            return unambig;
        });

        const sendConfirmation = Cart.prefersEmailReceipts.get() === true;
        const req: CreateOrderRequest = {
            clientVersion: CONSTANTS.VERSION,
            clientBuild: CONSTANTS.BUILD,
            idempotency_key: idempotencyKey,
            line_items: Cart.items.map(mapToCreateOrderReqLineItem),
            details: {
                email: state.email.trim(),
                needed_by_date: state.neededByDate || null,
                needed_by_time: state.neededByTime || null,
                phone: state.phone,
                placed_by: state.placedBy,
                send_confirmation: sendConfirmation,
            },
            shipping: shipping,
        };
        const api = new Orders(Auth.token.value);

        Sentry.addBreadcrumb({
            message: "Sending order creation request to server",
            category: "Kmmp.Browser.Cart.Details",
            level: "info",
            data: { idempotencyKey: idempotencyKey },
        });

        try {
            const result = await api.V2.create(req);

            Sentry.addBreadcrumb({
                message: "Order confirmed by server, navigating to confirmation page",
                category: "Kmmp.Browser.Cart.Details",
                level: "info",
                data: { displayId: result.display_id },
            });

            const totalImages = req.line_items.reduce((total, li) => countImages(li) + total, 0);

            // Capture a Sentry event if the user has somehow placed an order with more than one image per line item.
            if (totalImages > 1) {
                Sentry.captureEvent({
                    message: "User placing order with extra images.",
                    extra: {
                        totalImages: totalImages,
                        totalLineItems: req.line_items.length,
                    },
                    user: {
                        id: Auth.session?._id,
                        username: Auth.session?.location_id,
                        location_name: Auth.session?.location_name,
                    },
                });
            }

            // User's order has been placed. Send them to the confirmation page and reset the cart.
            const query = qs.stringify({
                display_id: result.display_id,
                email: result.email,
                requestedEmail: sendConfirmation,
            });

            sessionStorage.removeItem(IDEMPOTENCY_SESSION_KEY);

            try {
                Cart.reset();
            } catch (resetError) {
                // If localStorage quota is exceeded when resetting cart, log but don't block navigation.
                const sourceId = "b7e8c2f1-4a3d-4e9c-9b1a-8f6d3e5c2a1b";
                Sentry.captureException(resetError, {
                    level: "error",
                    extra: {
                        category: "Kmmp.Browser.Cart.Details.SuccessHandler",
                        sourceId: sourceId,
                        displayId: result.display_id,
                    },
                });
            }

            setLocation(`${Paths.confirmed.index}?${query}`, { replace: true });
        } catch (error) {
            const sourceId = "a000e55a-a550-4d9c-80a9-25d5c11e43df";
            const sentryCategory = "Kmmp.Browser.Cart.Details";
            const sentryLevel = error instanceof ApiError && error.unauthorized ? "warning" : "error";

            Sentry.addBreadcrumb({
                message: "Failed to place order",
                category: sentryCategory,
                level: sentryLevel,
            });
            Sentry.captureException(error, {
                level: sentryLevel,
                extra: {
                    category: sentryCategory,
                    sourceId: sourceId,
                    idempotencyKey: idempotencyKey,
                },
            });

            const returnPath = Auth.companySettings.siteType === "ecommerce" ? Paths.cart.index : Paths.cart.name;
            if (error instanceof ApiError && error.unauthorized && handleUnauthorized(returnPath))
                return;

            const message =
                error instanceof Error
                    ? error.message
                    : "Failed to place order, please try again.";

            setState(state => ({ ...state, placingOrder: false, error: Option.ofSome(`${message} (id: ${sourceId})`) }));
        }
    }

    const handleChangeRate = (e: React.FormEvent<HTMLInputElement>) => {
        const elValue = e.target["value"];
        let rate: AmbiguousRate | Rate;

        if (Cart.shipping.ambiguous === true) {
            rate = elValue;
        } else {
            const matchingRate = Cart.shipping.rates.find((rate) => rate.Service.Code === elValue);

            if (!matchingRate) {
                console.error("Failed to find matching rate with code " + elValue, Cart.shipping.rates);
                return;
            }

            rate = matchingRate;
        }

        setState(state => ({ ...state, selectedRate: rate }));
    };

    const handleChangePrefersEmailConfirmation = React.useCallback((e: React.FormEvent<HTMLInputElement>) => {
        Cart.setPrefersEmailReceipts(e.currentTarget.checked);
    }, [Cart.setPrefersEmailReceipts]);
    const handleChangeShippingMethodConsent = React.useCallback((e: React.FormEvent<HTMLInputElement>) => {
        const value = e.currentTarget.checked;
        setState(state => ({ ...state, shippingMethodConsent: value }));
    }, [setState]);

    const setPlacedBy = React.useCallback((e: React.FormEvent<HTMLInputElement>) => {
        const value = e.currentTarget.value;
        setState(state => ({ ...state, placedBy: value }));
    }, [setState]);
    const setEmail = React.useCallback((e: React.FormEvent<HTMLInputElement>) => {
        const value = e.currentTarget.value;
        setState(state => ({ ...state, email: value }));
    }, [setState]);
    const setPhone = React.useCallback((e: React.FormEvent<HTMLInputElement>) => {
        const value = e.currentTarget.value;
        setState(state => ({ ...state, phone: value }));
    }, [setState]);
    const setNeededByDate = React.useCallback((e: React.FormEvent<HTMLInputElement>) => {
        const value = e.currentTarget.value;
        setState(state => ({ ...state, neededByDate: value }));
    }, [setState]);
    const setNeededByTime = React.useCallback((e: React.FormEvent<HTMLInputElement>) => {
        const value = e.currentTarget.value;
        setState(state => ({ ...state, neededByTime: value }));
    }, [setState]);

    const description = (
        <p>
            {`Enter your details here and then place your order. `}
            <strong>{`You will only receive an email confirmation if you check the consent box below.`}</strong>
        </p>
    );
    const { placingOrder, error, selectedRate } = state;
    const { placedBy, email, phone, neededByDate, neededByTime } = state;
    let rates: JSX.Element[];

    if (Cart.shipping.ambiguous === true) {
        // TODO: re-enable this once we're reconnected to the UPS API
        const showExtraRates = false;
        let availableRates: string[];

        if (showExtraRates) {
            availableRates = CONSTANTS.AMBIGUOUS_RATES;

            if (showSaturdayRates()) {
                availableRates = [...availableRates, ...CONSTANTS.AMBIGUOUS_SATURDAY_RATES];
            }
        }
        else {
            availableRates = ["Ground", "Second Day Air", "Next Day Air"];
        }

        rates = availableRates.map((rate) => (
            <div key={rate} className="radio">
                <label htmlFor={`${rate}-radio`} className="cursor">
                    <input
                        type="radio"
                        id={`${rate}-radio`}
                        className="cursor"
                        style={{ marginRight: ".5rem" }}
                        name="shippingMethod"
                        value={rate}
                        checked={rate === selectedRate}
                        onChange={(e) => handleChangeRate(e)}
                    />
                    <span>{rate}</span>
                    <If condition={rate === "Next Day Air"}>
                        <span className={"red bold"} style={{color: "red"}}>
                            {" (May incur charges up to or exceeding $100.)"}
                        </span>
                    </If>
                </label>
            </div>
        ));
    } else {
        const selectedRate = state.selectedRate as Rate;

        rates = Cart.shipping.rates.map((rate) => (
            <div key={rate.Service.Code} className="radio">
                <label htmlFor={`${rate.Service.Code}-radio`} className="cursor">
                    <input
                        type="radio"
                        id={`${rate.Service.Code}-radio`}
                        className="cursor"
                        style={{ marginRight: ".5rem" }}
                        name="shippingMethod"
                        value={rate.Service.Code}
                        checked={rate.Service.Code === selectedRate.Service.Code}
                        onChange={(e) => handleChangeRate(e)}
                    />
                    {`$${getRateValue(rate).toFixed(2)} - ${rate.Service.Name}`}
                </label>
            </div>
        ));
    }

    let buttonText: JSX.Element;

    if (placingOrder) {
        buttonText = (
            <span>
                <Progress color={`#fff`} margin={`0 5px 0 0`} />
                {"Placing Order"}
            </span>
        );
    } else {
        buttonText = (
            <span>
                <CheckIcon className={`valign-sub left`} size={18} color={`#fff`} />
                {"Place Order"}
            </span>
        );
    }

    const footer = (
        <div className="align-children justify-space-between">
            {placingOrder ? (
                <span />
            ) : (
                <Link to={Paths.cart.shipping} className="btn">
                    Back
                </Link>
            )}
            <button type="button" className="btn blue" onClick={(e) => handleContinue(e)}>
                {buttonText}
            </button>
        </div>
    );

    const familyCharge =
        Auth.companySettings.companyName === "sci" ? Option.ofSome(getFamilyCharge().toFixed(2)) : Option.ofNone();

    return (
        <Container>
            <Box title={`Order Details.`} description={description} error={error.defaultValue("")} footer={footer}>
                <form id="detailsForm" onSubmit={(e) => handleContinue(e)}>
                    <div className="control-group">
                        <label htmlFor="name">{"Your Name"}</label>
                        <input
                            type="text"
                            name="name"
                            autoComplete="name"
                            value={placedBy || ""}
                            onChange={setPlacedBy}
                        />
                    </div>
                    <div className="control-group">
                        <label htmlFor="email">{"Your Email Address"}</label>
                        <input
                            type="text"
                            name="email"
                            autoComplete="work email"
                            value={email || ""}
                            onChange={setEmail}
                        />
                    </div>
                    <div className="control-group">
                        <label htmlFor="phone">{"Your Phone Number"}</label>
                        <input
                            type="tel"
                            name="phone"
                            autoComplete="work tel"
                            value={phone || ""}
                            onChange={setPhone}
                        />
                    </div>
                    <div className="pure-g">
                        <div className="pure-u-11-24">
                            <div className="control-group">
                                <label htmlFor="neededByDate">{"Date Needed By"}</label>
                                <input
                                    name="neededByDate"
                                    type="date"
                                    autoComplete="off"
                                    value={neededByDate || ""}
                                    onChange={setNeededByDate}
                                />
                            </div>
                        </div>
                        <div className="pure-u-2-24" />
                        <div className="pure-u-11-24">
                            <div className="control-group">
                                <label htmlFor="neededByTime">{"Time Needed By"}</label>
                                <input
                                    name="neededByTime"
                                    type="time"
                                    autoComplete="off"
                                    value={neededByTime || ""}
                                    onChange={setNeededByTime}
                                />
                            </div>
                        </div>
                    </div>
                    <hr />
                    <div className="pure-g">
                        <div className="pure-u-16-24">
                            <div className="control-group">
                                <label>{"Shipping Method"}</label>
                            </div>
                            {rates}
                        </div>

                        <If condition={false}>
                            {/* TODO: re-enable this once we're reconnected to the UPS API and can calculate shipping rates */}
                            {familyCharge
                                .map((charge) => (
                                    <div className="pure-u-8-24">
                                        <div className="control-group text-right">
                                            <label>{"Additional Shipping to Charge Family:"}</label>
                                            <h4 style={{ textAlign: "right" }}>{`$${charge}`}</h4>
                                        </div>
                                    </div>
                                ))
                                .defaultValue(<React.Fragment />)}
                        </If>
                    </div>
                    <div className="checkbox">
                        <label className="cursor" style={{ marginTop: "1.5rem" }}>
                            <input
                                type={`checkbox`}
                                style={{ marginRight: ".5rem" }}
                                checked={state.shippingMethodConsent}
                                onChange={handleChangeShippingMethodConsent}
                            />
                            {`I understand that the shipping method I've chosen may incur additional charges.`}
                        </label>
                    </div>
                    <div className="checkbox">
                        <label className="cursor" style={{ marginTop: "1.5rem" }}>
                            <Observer>
                                {() => (
                                    <input
                                        type={`checkbox`}
                                        style={{ marginRight: ".5rem" }}
                                        checked={Cart.prefersEmailReceipts.get() === true}
                                        onChange={handleChangePrefersEmailConfirmation}
                                    />
                                )}
                            </Observer>
                            {`Please send me an order confirmation message to the email address I've entered above.`}
                        </label>
                    </div>
                </form>
            </Box>
        </Container>
    );
}
